import Swiper from 'swiper';
import { A11y, Navigation, Pagination, Autoplay, EffectFade, Keyboard } from 'swiper/modules';

export default class Slider {
  constructor(theme, element, options) {
    this.theme = theme;
    this.element = element;

    this.restartTimer = null;
    this.running = true;
    this.speed = options.speed !== undefined ? options.speed : 200;
    this.transitionSpeed = options.transitionSpeed !== undefined ? options.transitionSpeed : 300;
    this.paginationType = options.paginationType;
    this.effect = options.animation || 'slide';
    this.slideChangeCallback = options.onSlideChange;
    this.desktopHeight = options.desktopHeight;
    this.mobileHeight = options.mobileHeight;
    this.isDraggable = options.isDraggable;
    this.blockIsActive = false;

    this.swiperModules = [
      A11y,
      Navigation,
      Pagination,
      Autoplay,
      EffectFade,
      Keyboard
    ]

    if (window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', () => {
        this.blockIsActive = true;
      });

      document.addEventListener('shopify:block:deselect', () => {
        this.blockIsActive = false;
      });
    }

    // Determine if we should use autoHeight
    this.setAutoHeight();

    this.swiper = this.configure();

    // Handle window resize - we'll only ever see a significant resize when in editor (mobile => desktop or vice versa)
    if (window.Shopify.designMode) {
      window.addEventListener('resize', () => {
        const index_currentSlide = this.swiper?.activeIndex;

        this.setAutoHeight();
        this.swiper?.destroy();
        this.swiper = this.configure();

        this.swiper?.slideTo(index_currentSlide, 0);

        // stop autoplay when in editor
        if (this.blockIsActive) this.stop();
      });
    }
  }

  stop = () => {
    if (this.element.querySelectorAll('.swiper-slide').length <= 1) {
      return;
    }
    this.swiper.autoplay.stop();
  }

  start = () => {
    if (this.element.querySelectorAll('.swiper-slide').length <= 1) {
      return;
    }
    this.swiper.autoplay.start();
  }

  isRunning = () => {
    return this.swiper.autoplay.running;
  }

  /**
   * @param {number} index
   */
  slideTo = (index, skipAnimation = false) => {
    this.swiper.slideTo(index, skipAnimation ? 0 : 200);
  }

  /**
   * @param {number} index
   */
  slideToLoop = (index, skipAnimation = false) => {
    this.swiper.slideToLoop(index, skipAnimation ? 0 : 200);
  }

  onSlideChange = () => {
    if (this.slideChangeCallback) {
      this.slideChangeCallback();
    }
  };

  setAutoHeight = () => {
    this.autoHeightEnabled = false;
    if (this.desktopHeight && this.mobileHeight) {
      if (!this.isMobile() && this.desktopHeight == 'original') {
        this.autoHeightEnabled = true;
      } else if (this.isMobile() && this.mobileHeight == 'original') {
        this.autoHeightEnabled = true;
      }
    }

    return this.autoHeightEnabled;
  }

  configure = () => {
    let paginationStyle = this.paginationType;
    let paginationElement = this.element.querySelector('.swiper-pagination');

    if (this.element.querySelectorAll('.swiper-slide').length <= 1) {
      return;
    }

    const swiper = new Swiper(this.element, {
      modules: this.swiperModules,
      allowTouchMove: this.isDraggable == 'true' ? true : false,
      autoHeight: this.autoHeightEnabled,
      loop: true,
      loopedSlides: this.effect === 'fade' ? 0 : 2,
      autoplay: this.speed > 0 ? {
        delay: this.speed,
        disableOnInteraction: false,
      } : false,
      effect: this.effect,
      slidesPerView: 1,
      speed: this.transitionSpeed,
      watchOverflow: true,
      keyboard: {
        enabled: true,
        onlyInViewport: false
      },
      fadeEffect: { crossFade: true },
      navigation: {
        nextEl: this.element.querySelector('.flex-next'),
        prevEl: this.element.querySelector('.flex-prev'),
      },
      pagination: {
        el: this.element.querySelector('.swiper-pagination'),
        type: paginationStyle,
        renderBullet: function (index, className) {
          const paginationOption = paginationElement.dataset.paginationStyle;
          if (paginationOption == 'bars') {
            return '<span class="' + className + ' progress-bar">' + '<i></i>' + '<b></b>'  + '</span>';
          } else if (paginationOption == 'dots') {
            return '<span class="' + className + '">' + '<i></i>' + '<b></b>'  + '</span>';
          }
        },
        clickable: true,
      }
    });
    swiper.on('realIndexChange', () => { // realIndexChange is more reliable than slideChange for loop mode
      const event = new CustomEvent('slider:slide:change');
      document.dispatchEvent(event);
      this.onSlideChange();
    });
    return swiper;
  };

  // check if we're on mobile
  isMobile = () => {
    return window.matchMedia('(max-width: 1023px)').matches;
  }
}
